// src/components/NavbarMobile.jsx
import React, { useEffect, useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import "../pages/styles/navbar.css";

import { AiOutlineHome, AiOutlineAppstoreAdd } from "react-icons/ai";
import { FiShoppingCart } from "react-icons/fi";
import { IoHeartOutline, IoPersonOutline } from "react-icons/io5";

const GUEST_CART_KEY = "kupto_guest_cart_v1";

const readGuestCart = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(GUEST_CART_KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
};

const NavbarMobile = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const authState = useSelector((state) => state?.auth || {});
  const cartState = useSelector((state) => state?.auth?.cartProduct);

  const isLoggedIn = Boolean(
    authState?.user?.token || localStorage.getItem("token")
  );

  // ✅ Keep normal bottom padding.
  // The Header.js search overlay class + CSS will hide the nav when searching.
  useEffect(() => {
    document.body.classList.add("hasBottomNav");

    return () => {
      document.body.classList.remove("hasBottomNav");
    };
  }, []);

  const backendList = useMemo(() => {
    if (Array.isArray(cartState)) return cartState;
    if (cartState && Array.isArray(cartState?.cart)) return cartState.cart;
    return [];
  }, [cartState]);

  const cartQty = useMemo(() => {
    const list = isLoggedIn ? backendList : readGuestCart();

    return (list || []).reduce(
      (acc, it) => acc + Number(it?.quantity ?? it?.qty ?? 0),
      0
    );
  }, [isLoggedIn, backendList]);

  const returnTo = useMemo(
    () => location.pathname + location.search,
    [location.pathname, location.search]
  );

  const goIdentification = () =>
    navigate("/identification", { state: { returnTo } });

  const onAccount = () => {
    if (isLoggedIn) navigate("/my-profile");
    else goIdentification();
  };

  const onWishlist = () => {
    if (isLoggedIn) navigate("/whishlist");
    else goIdentification();
  };

  const isActivePath = (p) => {
    const cur = location.pathname || "";
    if (p === "/") return cur === "/";
    return cur.startsWith(p);
  };

  return (
    <nav className="jmNav-wrap" aria-label="Bottom navigation">
      <div className="jmNav-bar">
        <NavLink
          to="/"
          className={() => `jmNav-item ${isActivePath("/") ? "is-active" : ""}`}
          aria-label="Home"
        >
          <AiOutlineHome className="jmNav-ico" />
          <span className="jmNav-label">Home</span>
        </NavLink>

        <NavLink
          to="/product"
          className={() =>
            `jmNav-item ${isActivePath("/product") ? "is-active" : ""}`
          }
          aria-label="Store"
        >
          <AiOutlineAppstoreAdd className="jmNav-ico" />
          <span className="jmNav-label">Store</span>
        </NavLink>

        <NavLink
          to="/cart"
          className={() =>
            `jmNav-item ${isActivePath("/cart") ? "is-active" : ""}`
          }
          aria-label="Cart"
        >
          <span className="jmNav-icoWrap">
            <FiShoppingCart className="jmNav-ico" />
            {cartQty > 0 ? (
              <span className="jmNav-badge" aria-label={`${cartQty} items`}>
                {cartQty > 99 ? "99+" : cartQty}
              </span>
            ) : null}
          </span>
          <span className="jmNav-label">Cart</span>
        </NavLink>

        <button
          type="button"
          className={`jmNav-item jmNav-btn ${
            isActivePath("/whishlist") ? "is-active" : ""
          }`}
          onClick={onWishlist}
          aria-label="Wishlist"
        >
          <IoHeartOutline className="jmNav-ico" />
          <span className="jmNav-label">Wishlist</span>
        </button>

        <button
          type="button"
          className={`jmNav-item jmNav-btn ${
            isActivePath("/my-profile") ? "is-active" : ""
          }`}
          onClick={onAccount}
          aria-label="Account"
        >
          <IoPersonOutline className="jmNav-ico" />
          <span className="jmNav-label">Account</span>
        </button>
      </div>
    </nav>
  );
};

export default NavbarMobile;